/**
 * ShadowGlider.ts
 * World 5 (Eclipse Bastion) enemy — Nova's own shadow on a 2-second delay.
 *
 * Each tick the glider records Nova's current position into a ring buffer
 * and teleports to the position Nova held 2 seconds ago. There is no
 * gravity, no platform collision, and no AI decision-making: the glider's
 * entire behaviour is "be where Nova was 120 ticks ago". For the first
 * 2 seconds after spawn the buffer is filled with the spawn position, so
 * the glider holds at spawn until enough history has accumulated.
 *
 * State machine: SHADOWING → HURT → DEAD
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 5 (Eclipse Bastion) stub. Wired into levels when W5 ships.
 * See ROADMAP.md Phase 4 (`ShadowGlider.ts`).
 *
 * @module ShadowGlider
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import Logger from '../../utils/logger';

export type ShadowGliderState = 'SHADOWING' | 'HURT' | 'DEAD';

/** Width/height of the ShadowGlider hitbox in logical pixels. */
export const SHADOW_GLIDER_WIDTH = 22;
export const SHADOW_GLIDER_HEIGHT = 28;

// DECISION: 120 ticks at the fixed 1/60 step = exactly 2 s of history. Using
// a power-of-two-adjacent constant rather than deriving from a DURATION_S so
// the ring-buffer modulo and the "spawn-hold" window are both exact.
const HISTORY_FRAMES = 120;

/** Duration of the hurt-flash before death in seconds. */
const HURT_DURATION = 0.25;

/** Render colour for greybox — shadow violet. */
export const SHADOW_GLIDER_COLOR = '#3a2b58';

/**
 * Projectile-impact result. Shares the union shape with the other shieldless
 * Phase 4 enemies (SparkImp, Gearling) so combat callers see one contract.
 *
 * DECISION: No `'KILLED'` value — `applyShardHit()` never sets `dead = true`
 * synchronously. The HURT → DEAD transition completes inside `update()`
 * once the hurt timer elapses. `'BLOCKED'` is reserved for shielded variants
 * and never returned today.
 */
export type ShadowGliderHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/**
 * ShadowGlider — World 5 enemy that mirrors Nova's last 2 s of movement.
 *
 * Spawn with `new ShadowGlider(x, y)`. Call `update(dt, level, nova)` each
 * fixed tick — `level` is accepted to satisfy the shared `Enemy` contract
 * but is ignored (the glider is a ghost and does not collide with platforms).
 * Read `dead` to know when to remove from the scene.
 */
export class ShadowGlider extends BaseEntity {
  public readonly entityType = 'ShadowGlider';
  public readonly color = SHADOW_GLIDER_COLOR;
  public state: ShadowGliderState = 'SHADOWING';

  /**
   * True once a hit has started the death sequence (`HURT`) or it has
   * finished (`DEAD`). See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'HURT' || this.state === 'DEAD';
  }
  /** Direction the glider's sprite should face. Inferred from teleport delta. */
  public facingRight = true;

  // DECISION: Float32Array instead of a {x,y}[] ring — fixed allocation, no
  // GC churn from per-tick object snapshots, and indexing is faster.
  private _histX: Float32Array;
  private _histY: Float32Array;
  private _histHead = 0;
  private _hurtTimer = 0;

  constructor(x: number, y: number) {
    super(x, y, SHADOW_GLIDER_WIDTH, SHADOW_GLIDER_HEIGHT);
    this._histX = new Float32Array(HISTORY_FRAMES);
    this._histY = new Float32Array(HISTORY_FRAMES);
    // Pre-fill with the spawn position so reads during the first 2 s
    // resolve to (x, y) — the glider holds at spawn until history accumulates.
    this._histX.fill(x);
    this._histY.fill(y);
  }

  /**
   * Advance the glider by one fixed tick.
   * @param dt     Fixed-step delta in seconds.
   * @param _level Current level geometry — ignored (ghost; no platform collisions).
   * @param nova   Optional Nova snapshot. Undefined → glider freezes in place.
   */
  public update(dt: number, _level: LevelRuntime, nova?: NovaRuntime): void {
    if (this.state === 'DEAD') return;

    if (this.state === 'HURT') {
      this._hurtTimer -= dt;
      if (this._hurtTimer <= 0) {
        this.state = 'DEAD';
        this.dead = true;
        Logger.debug('ShadowGlider', 'Death after hurt');
      }
      return;
    }

    // SHADOWING. Without a Nova snapshot we cannot advance the timeline, so
    // freeze position rather than tear a hole in the recorded history.
    if (!nova) return;

    // DECISION: Read BEFORE writing so the delay is exactly HISTORY_FRAMES
    // ticks (= 2.0 s at 1/60). The slot at `_histHead` holds the position
    // from exactly HISTORY_FRAMES ticks ago — it is the one we are about to
    // overwrite with the current sample. Reading after the advance instead
    // (write -> advance -> read) would give a HISTORY_FRAMES - 1 tick lag
    // (1.983 s), which the original implementation had until #98 review.
    // Pre-fill of the buffer with spawn (x, y) means the glider naturally
    // holds at spawn for the first HISTORY_FRAMES ticks.
    const prevX = this.x;
    this.x = this._histX[this._histHead];
    this.y = this._histY[this._histHead];
    this._histX[this._histHead] = nova.x;
    this._histY[this._histHead] = nova.y;
    this._histHead = (this._histHead + 1) % HISTORY_FRAMES;
    if (this.x !== prevX) this.facingRight = this.x > prevX;
  }

  /**
   * Apply one projectile hit. No shield — any shard connects.
   * @returns `'HURT'` on a hit that started the death sequence, `'IGNORED'`
   *          if already dead/hurt. Never returns `'BLOCKED'` today.
   */
  public applyShardHit(): ShadowGliderHitResult {
    if (this.state === 'DEAD' || this.state === 'HURT') return 'IGNORED';
    this._enterHurt();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. No protection from above.
   * @returns `true` if the stomp started the death sequence; `false` if already dead/hurt.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD' || this.state === 'HURT') return false;
    this._enterHurt();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts) — delegates to
   * the specialised methods. Kept thin so `combatStep` can drive ShadowGlider
   * without an external adapter once it is wired into `gameState.enemies`.
   * @param fromStomp `true` for stomp contact, `false`/omitted for shard hits.
   * @returns `true` if this hit started the death sequence; `false` otherwise.
   */
  public applyHit(fromStomp = false): boolean {
    return fromStomp ? this.applyStomp() : this.applyShardHit() === 'HURT';
  }

  private _enterHurt(): void {
    this.state = 'HURT';
    this._hurtTimer = HURT_DURATION;
    Logger.debug('ShadowGlider', 'Hit — entering HURT');
  }
}
